import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import ScrollableContainer from '../scrollable-container';
import buildAssetPath from '../../utils/build-asset-path';
import { useTheme } from '../../stores/theme';
import clsx from 'clsx';
import RoleAvatarPreview from './role-avatar-preview';
import MeetingControls from './meeting-controls';
import Volume from '../../assets/icons/volume';

const MeetingRoom = () => {
  const [currentElementId, setCurrentElementId] = useState(data[0].id);
  const images = useTheme(({ images }) => images);

  useEffect(() => {
    for (const d of data) {
      const observer = new IntersectionObserver(
        entries => {
          const entry = entries[0];
          if (entry.isIntersecting) setCurrentElementId(d.id);
        },
        { threshold: 0.5 }
      );
      observer.observe(document.getElementById(d.id));
    }
  }, []);

  return (
    <div className="flex w-full h-full m-auto p-4 overflow-hidden">
      <div className="w-2/3 h-full flex">
        <div className="computer-container w-full">
          <div className="computer-monitor w-full h-4/5 flex flex-col p-4">
            <div className="flex flex-grow h-full">
              <div className="w-2/3 h-full grid grid-rows-2">
                <div className="flex">
                  {data
                    .slice(0, 3)
                    .filter(el => el.id !== currentElementId)
                    .map((el, i) => (
                      <RoleAvatarPreview
                        key={el.id}
                        name={el.id}
                        className={clsx(
                          'first:rounded-tl-lg border border-t-2 border-l-2'
                        )}
                        avatar={images(el.image)}
                        handleClick={() => {
                          setCurrentElementId(el.id);
                          const element = document.getElementById(el.id);
                          if (element) {
                            element.scrollIntoView({ behavior: 'smooth' });
                          }
                        }}
                      />
                    ))}
                </div>
                <div className="flex">
                  {data
                    .slice(3, 6)
                    .filter(el => el.id !== currentElementId)
                    .map(el => (
                      <RoleAvatarPreview
                        key={el.id}
                        name={el.id}
                        className="border border-b-2 border-l-2"
                        avatar={images(el.image)}
                        handleClick={() => {
                          setCurrentElementId(el.id);
                          const element = document.getElementById(el.id);
                          if (element) {
                            element.scrollIntoView({ behavior: 'smooth' });
                          }
                        }}
                      />
                    ))}
                </div>
              </div>
              <div className="w-1/3 h-full relative border-2 border-l rounded-tr-lg border-accountingGreen-light">
                {data.map(el => (
                  <motion.div
                    key={el.id}
                    className={clsx(
                      'absolute h-full w-full flex-grow bg-center bg-cover bg-no-repeat'
                    )}
                    style={{
                      backgroundPositionY: '30px',
                      backgroundImage: `url('${images(el.image)}')`,
                    }}
                    animate={{
                      opacity: el.id === currentElementId ? 1 : 0,
                    }}
                    transition={{
                      duration: 0.5,
                      delay: el.id === currentElementId ? 0.5 : 0,
                    }}
                  >
                    <span className="relative left-3 top-3 bg-accountingBlue-dark bg-opacity-90 text-white text-sm px-3 py-1 rounded-lg leading-none">
                      <Volume className="inline fill-accountingGreen-light h-4 align-middle mr-2" />
                      {el.id}
                    </span>
                  </motion.div>
                ))}
              </div>
            </div>
            <MeetingControls />
          </div>
          <div className="computer-bottom w-full"></div>
          <div className="computer-stand"></div>
          <div className="computer-stand-bottom"></div>
        </div>
      </div>
      <div
        id="meeting-room-content"
        className="w-1/3 bg-accountingBlue-light rounded-2xl h-full p-4 flex flex-col space-y-4 text-white ml-6"
      >
        <ScrollableContainer>
          {data.map((d, i) => (
            <div key={i} id={d.id}>
              <h3 className="my-4">{d.title}</h3>
              <p>{d.content}</p>
            </div>
          ))}
        </ScrollableContainer>
      </div>
    </div>
  );
};

export default MeetingRoom;

const data = [
  {
    id: 'BOARD',
    title: 'What is Lorem Ipsum?',
    content: `Lorem Ipsum is simply dummy text of the printing and typesetting
              industry. Lorem Ipsum has been the industry's standard dummy text
              ever since the 1500s, when an unknown printer took a galley of
              type and scrambled it to make a type specimen book. It has
              survived not only five centuries, but also the leap into
              electronic typesetting, remaining essentially unchanged. It was
              popularised in the 1960s with the release of Letraset sheets
              containing Lorem Ipsum passages, and more recently with desktop
              publishing software like Aldus PageMaker including versions of
              Lorem Ipsum.`,
    image: buildAssetPath('avatars/Avatars-01.svg'),
    imageCaption: '',
  },
  {
    id: 'CEO',
    title: 'What is Lorem Ipsum?',
    content: `Lorem Ipsum is simply dummy text of the printing and typesetting
              industry. Lorem Ipsum has been the industry's standard dummy text
              ever since the 1500s, when an unknown printer took a galley of
              type and scrambled it to make a type specimen book. It has
              survived not only five centuries, but also the leap into
              electronic typesetting, remaining essentially unchanged. It was
              popularised in the 1960s with the release of Letraset sheets
              containing Lorem Ipsum passages, and more recently with desktop
              publishing software like Aldus PageMaker including versions of
              Lorem Ipsum.`,
    image: buildAssetPath('avatars/Avatars-02.svg'),
    imageCaption: '',
  },
  {
    id: 'COO',
    title: 'Why do we use it?',
    content: `It is a long established fact that a reader will be distracted by
              the readable content of a page when looking at its layout. The
              point of using Lorem Ipsum is that it has a more-or-less normal
              distribution of letters, as opposed to using 'Content here,
              content here', making it look like readable English. Many desktop
              publishing packages and web page editors now use Lorem Ipsum as
              their default model text, and a search for 'lorem ipsum' will
              uncover many web sites still in their infancy. Various versions
              have evolved over the years, sometimes by accident, sometimes on
              purpose (injected humour and the like).`,
    image: buildAssetPath('avatars/Avatars-03.svg'),
    imageCaption: '',
  },
  {
    id: 'CFO',
    title: 'Where does it come from?',
    content: `Contrary to popular belief, Lorem Ipsum is not simply random text.
              It has roots in a piece of classical Latin literature from 45 BC,
              making it over 2000 years old. Richard McClintock, a Latin
              professor at Hampden-Sydney College in Virginia, looked up one of
              the more obscure Latin words, consectetur, from a Lorem Ipsum
              passage, and going through the cites of the word in classical
              literature, discovered the undoubtable source. Lorem Ipsum comes
              from sections 1.10.32 and 1.10.33 of "de Finibus Bonorum et
              Malorum" (The Extremes of Good and Evil) by Cicero, written in 45
              BC. This book is a treatise on the theory of ethics, very popular
              during the Renaissance. The first line of Lorem Ipsum, "Lorem
              ipsum dolor sit amet..", comes from a line in section 1.10.32. The
              standard chunk of Lorem Ipsum used since the 1500s is reproduced
              below for those interested. Sections 1.10.32 and 1.10.33 from "de
              Finibus Bonorum et Malorum" by Cicero are also reproduced in their
              exact original form, accompanied by English versions from the 1914
              translation by H. Rackham.`,
    image: buildAssetPath('avatars/Avatars-04.svg'),
    imageCaption: '',
  },
  {
    id: 'CMO',
    title: 'Where can I get some?',
    content: `There are many variations of passages of Lorem Ipsum available,
              but the majority have suffered alteration in some form, by
              injected humour, or randomised words which don't look even
              slightly believable. If you are going to use a passage of Lorem
              Ipsum, you need to be sure there isn't anything embarrassing
              hidden in the middle of text. All the Lorem Ipsum generators on
              the Internet tend to repeat predefined chunks as necessary, making
              this the first true generator on the Internet. It uses a
              dictionary of over 200 Latin words, combined with a handful of
              model sentence structures, to generate Lorem Ipsum which looks
              reasonable. The generated Lorem Ipsum is therefore always free
              from repetition, injected humour, or non-characteristic words etc.`,
    image: buildAssetPath('avatars/Avatars-05.svg'),
    imageCaption: '',
  },
  {
    id: 'CPO',
    title: 'Last but not least',
    content: `Tuttavia, perché voi intendiate da dove sia nato tutto questo errore, di quelli che incolpano il piacere ed esaltano il dolore, io spiegherò tutta la questione, e presenterò le idee espresse dal famoso esploratore della verità, vorrei quasi dire dal costruttore della felicità umana. Nessuno, infatti, detesta, odia, o rifugge il piacere in quanto tale, solo perché è piacere, ma perché grandi sofferenze colpiscono quelli che non sono capaci di raggiungere il piacere attraverso la ragione; e al contrario, non c'è nessuno che ami, insegua, voglia raggiungere il dolore in sé stesso, soltanto perché è dolore, ma perché qualche volta accadono situazioni tali per cui attraverso la sofferenza o il dolore si cerca di raggiungere un qualche grande piacere. Concentrandoci su casi di piccola importanza: chi di noi intraprende un esercizio ginnico, se non per ottenerne un qualche vantaggio? E d'altra parte, chi avrebbe motivo di criticare colui che desidera provare un piacere cui non segua nessun fastidio, o colui che fugge un dolore che non produce nessun piacere?`,
    image: buildAssetPath('avatars/Avatars-06.svg'),
    imageCaption: '',
  },
];
